import { type Day, type CutoffTime } from '@choco-admin/types';
import { Spinner } from '@heroui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { BaseModal } from '../../../../../../../components/BaseModal';
import { DeliveryDaySettings } from '../../../../../../../components/DeliveryDaySettings';
import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useSupplierDeliverySettings } from '../../../../../hooks/useSupplierDeliverySettings';
import { useUpdateDeliverySettings } from '../../../../../hooks/useUpdateDeliverySettings';
import { hasAnyDeliveryDay } from '../../../../../utils/deliveryDays';

import {
  CutoffTypeSelect,
  type CutoffType,
} from './components/CutoffTypeSelect';
import { DAYS } from './deliverySettingsView';

interface DeliverySettingsFormData {
  cutoffType: CutoffType;
  deliveryDays: Record<Day, boolean>;
  cutoffTimes: Record<Day, CutoffTime>;
  cutoffDays: Record<Day, string>;
}

interface DeliverySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplierId: string;
}

const DEFAULT_DELIVERY_DAYS: Record<Day, boolean> = {
  Monday: false,
  Tuesday: false,
  Wednesday: false,
  Thursday: false,
  Friday: false,
  Saturday: false,
  Sunday: false,
};

const DEFAULT_CUTOFF_TIMES: Record<Day, CutoffTime> = {
  Monday: '23:59:59',
  Tuesday: '23:59:59',
  Wednesday: '23:59:59',
  Thursday: '23:59:59',
  Friday: '23:59:59',
  Saturday: '23:59:59',
  Sunday: '23:59:59',
};

const DEFAULT_CUTOFF_DAYS: Record<Day, string> = {
  Monday: '1',
  Tuesday: '1',
  Wednesday: '1',
  Thursday: '1',
  Friday: '1',
  Saturday: '1',
  Sunday: '1',
};

const DEFAULT_VALUES: DeliverySettingsFormData = {
  cutoffType: 'no_cutoff',
  deliveryDays: DEFAULT_DELIVERY_DAYS,
  cutoffTimes: DEFAULT_CUTOFF_TIMES,
  cutoffDays: DEFAULT_CUTOFF_DAYS,
};

/**
 * Distributor delivery days & cutoff times, with its own form, its own Save and
 * its own mutation — the same shape as the chat-level modal on the Chats page.
 *
 * The schedule used to be fields on the distributor form, saved by its Save
 * Changes button. Because `cutoffTimes` is persisted as a full-map replace, one
 * profile save rewrote the whole schedule, and a stale form could put back days
 * somebody else had just changed. Owning the mutation here means a profile save
 * cannot reach the schedule at all, rather than being trusted not to.
 *
 * There is no "enable custom settings" checkbox, unlike the chat modal. A chat
 * can fall back to its distributor's schedule, so there the checkbox chooses
 * between custom and inherited. A distributor has nothing above it to inherit
 * from — its schedule is the company default — so the same control there was
 * only ever able to hide the section it sat above.
 */
export const DeliverySettingsModal: React.FC<DeliverySettingsModalProps> = ({
  isOpen,
  onClose,
  supplierId,
}) => {
  const { mappedSettings, isLoading, refetch } = useSupplierDeliverySettings({
    supplierId,
  });
  const { addNotification } = useNotification();
  const [isSaving, setIsSaving] = useState(false);

  const { updateDeliverySettings } = useUpdateDeliverySettings({ supplierId });

  const formMethods = useForm<DeliverySettingsFormData>({
    defaultValues: DEFAULT_VALUES,
  });
  const {
    watch,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = formMethods;

  // Seed from the loaded settings once per open, then leave the form alone.
  // `mappedSettings` is rebuilt on every render, so without the ref this would
  // re-seed continuously and discard whatever was being edited.
  const seeded = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      seeded.current = false;
      return;
    }
    if (seeded.current || !mappedSettings) return;

    reset({
      cutoffType: mappedSettings.cutoffType as CutoffType,
      deliveryDays: mappedSettings.deliveryDays,
      cutoffTimes: mappedSettings.cutoffTimes as Record<Day, CutoffTime>,
      cutoffDays: mappedSettings.cutoffDays,
    });
    seeded.current = true;
  }, [isOpen, mappedSettings, reset]);

  const cutoffType = watch('cutoffType') ?? 'no_cutoff';
  const deliveryDays = watch('deliveryDays') ?? DEFAULT_DELIVERY_DAYS;
  const cutoffTimes = watch('cutoffTimes') ?? DEFAULT_CUTOFF_TIMES;
  const cutoffDays = watch('cutoffDays') ?? DEFAULT_CUTOFF_DAYS;

  const handleDayToggle = useCallback(
    (day: Day) => {
      setValue(`deliveryDays.${day}`, !deliveryDays[day], {
        shouldDirty: true,
      });
    },
    [setValue, deliveryDays]
  );

  const handleCutoffTimeChange = useCallback(
    (day: Day, time: CutoffTime) => {
      setValue(`cutoffTimes.${day}`, time, { shouldDirty: true });
    },
    [setValue]
  );

  const handleCutoffDayChange = useCallback(
    (day: Day, cutoffDay: string) => {
      setValue(`cutoffDays.${day}`, cutoffDay, { shouldDirty: true });
    },
    [setValue]
  );

  const handleSave = useCallback(async () => {
    const values = getValues();

    // `buildCutoffTimes` drops every disabled day, so a schedule with none left
    // on is sent as an empty map and refused by the backend in its own words,
    // which say nothing about the day that was switched off. Refuse it here
    // instead, before anything is written.
    if (!hasAnyDeliveryDay(values.deliveryDays)) {
      addNotification({
        title: 'Delivery settings not saved',
        message: 'Turn on at least one delivery day before saving.',
        type: 'error',
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateDeliverySettings(values);
      // Refresh the summary behind the modal, and make the values just saved the
      // new baseline so Save disables until something changes again.
      await refetch();
      reset(values);
      addNotification({
        title: 'Saved',
        message: 'Delivery settings updated',
        type: 'success',
      });
      onClose();
    } catch (error) {
      // The backend's own message: it knows why it refused and this screen does
      // not, so replacing it with something generic drops the only part the
      // reader can act on.
      addNotification({
        title: 'Delivery settings not saved',
        message:
          error instanceof Error && error.message
            ? error.message
            : 'Delivery settings did not save. Try again.',
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    getValues,
    updateDeliverySettings,
    refetch,
    reset,
    addNotification,
    onClose,
  ]);

  return (
    <BaseModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="2xl"
      title="Delivery Days & Cutoff Times"
      subtitle="Delivery days and cutoff times for this distributor. These settings apply for all products."
      footerContent={{
        cancelText: 'Cancel',
        confirmText: 'Save',
        onCancel: onClose,
        onConfirm: handleSave,
        isLoading: isSaving,
        isDisabled: !isDirty,
      }}
    >
      {isLoading && !mappedSettings ? (
        <div className="flex justify-center py-10">
          <Spinner label="Loading delivery settings…" />
        </div>
      ) : (
        <FormProvider {...formMethods}>
          <div className="space-y-6">
            <CutoffTypeSelect />

            <div className="space-y-4">
              {DAYS.map((day) => (
                <DeliveryDaySettings
                  key={day}
                  day={day}
                  isEnabled={deliveryDays[day] || false}
                  cutoffTime={cutoffTimes[day] || '23:59:59'}
                  cutoffDay={cutoffDays[day] || '1'}
                  onToggle={handleDayToggle}
                  onCutoffTimeChange={handleCutoffTimeChange}
                  onCutoffDayChange={handleCutoffDayChange}
                  cutoffType={cutoffType}
                />
              ))}
            </div>
          </div>
        </FormProvider>
      )}
    </BaseModal>
  );
};
